import type { UserInfo } from "./index";

/**
 * Member-configurable name/email visibility preferences (#2193, §4).
 *
 * A member controls how much of their name and email the general public and
 * fellow club members see. Officers of the member's own club, and the member
 * themselves, always see full detail and are not governed by these preferences.
 *
 * Wire format: the backend serialises the tier values (uPickle sealed traits) as
 * the bare case-object name (`"Shortened"`). The release before it, which a
 * rollback returns to, sends the fully-qualified name
 * (`"toasthost.datamodel.user.NameFormat.Shortened"`), so `normalizeUserInfo`
 * accepts both. `serializeNamePrivacy` / `serializeEmailPrivacy` write the bare
 * name. Formats are listed low-reveal first so array index doubles as the
 * monotonicity ordering.
 */

export const NAME_FORMATS = ["Shortened", "Full"] as const;
export const EMAIL_FORMATS = ["Hidden", "Masked", "Full"] as const;

export type NameFormat = (typeof NAME_FORMATS)[number];
export type EmailFormat = (typeof EMAIL_FORMATS)[number];

export interface NamePrivacy {
  publicTier: NameFormat;
  memberTier: NameFormat;
}

export interface EmailPrivacy {
  publicTier: EmailFormat;
  memberTier: EmailFormat;
}

/** Most-private fallbacks, matching the backend defaults' safe end. */
const DEFAULT_NAME_FORMAT: NameFormat = "Shortened";
const DEFAULT_EMAIL_FORMAT: EmailFormat = "Hidden";

/** Backend `NamePrivacy.default` / `EmailPrivacy.default` (#2193 §4.3). */
export const DEFAULT_NAME_PRIVACY: NamePrivacy = { publicTier: "Shortened", memberTier: "Full" };
export const DEFAULT_EMAIL_PRIVACY: EmailPrivacy = { publicTier: "Hidden", memberTier: "Masked" };

/** How much a format reveals; higher reveals more. Position in the ordering array. */
export function nameRevealLevel(format: NameFormat): number {
  return NAME_FORMATS.indexOf(format);
}

export function emailRevealLevel(format: EmailFormat): number {
  return EMAIL_FORMATS.indexOf(format);
}

/** Member tier must reveal at least as much as the public tier (#2193 §4.4). */
export function isNamePrivacyValid(prefs: NamePrivacy): boolean {
  return nameRevealLevel(prefs.memberTier) >= nameRevealLevel(prefs.publicTier);
}

export function isEmailPrivacyValid(prefs: EmailPrivacy): boolean {
  return emailRevealLevel(prefs.memberTier) >= emailRevealLevel(prefs.publicTier);
}

function bareCaseName(raw: string): string {
  return raw.includes(".") ? raw.slice(raw.lastIndexOf(".") + 1) : raw;
}

/** Accepts the wire form or the bare case name; falls back to the private default. */
export function parseNameFormat(raw: string | null | undefined): NameFormat {
  const name = bareCaseName(raw ?? "");
  return (NAME_FORMATS as readonly string[]).includes(name) ? (name as NameFormat) : DEFAULT_NAME_FORMAT;
}

export function parseEmailFormat(raw: string | null | undefined): EmailFormat {
  const name = bareCaseName(raw ?? "");
  return (EMAIL_FORMATS as readonly string[]).includes(name) ? (name as EmailFormat) : DEFAULT_EMAIL_FORMAT;
}

/** Build the wire payload the backend `UpdateUserProfile` DTO expects. */
export function serializeNamePrivacy(prefs: NamePrivacy): { publicTier: string; memberTier: string } {
  return { publicTier: prefs.publicTier, memberTier: prefs.memberTier };
}

export function serializeEmailPrivacy(prefs: EmailPrivacy): { publicTier: string; memberTier: string } {
  return { publicTier: prefs.publicTier, memberTier: prefs.memberTier };
}

/**
 * Normalise the privacy tiers of a `UserInfo` from the wire form to the bare
 * case names the UI works with. Idempotent (bare names pass through unchanged),
 * so it is safe to apply at every read boundary.
 */
export function normalizeUserInfo(user: UserInfo): UserInfo {
  const normalized: UserInfo = { ...user };
  if (user.namePrivacy) {
    normalized.namePrivacy = {
      publicTier: parseNameFormat(user.namePrivacy.publicTier),
      memberTier: parseNameFormat(user.namePrivacy.memberTier),
    };
  }
  if (user.emailPrivacy) {
    normalized.emailPrivacy = {
      publicTier: parseEmailFormat(user.emailPrivacy.publicTier),
      memberTier: parseEmailFormat(user.emailPrivacy.memberTier),
    };
  }
  return normalized;
}

/**
 * Render a display name at the requested format. Port of the backend
 * `IdentityMasking.maskName`, used to drive the live per-tier preview.
 *
 *   - `Full` returns the name unchanged ("Fred Bloggs").
 *   - `Shortened` keeps the first whitespace token and reduces the rest to their
 *     first character plus `*` ("Fred Bloggs" -> "Fred B*", "DJ Fred" -> "DJ F*").
 */
export function maskNameAtFormat(displayName: string, format: NameFormat): string {
  if (format === "Full") return displayName;
  const tokens = displayName.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return displayName.trim();
  const [first, ...rest] = tokens;
  return [first, ...rest.map(reduceLabel)].join(" ");
}

/**
 * Render an email at the requested format. Port of the backend
 * `IdentityMasking.maskEmail`; `Hidden` yields an empty string, which
 * `<UserEmail>` renders as the withheld placeholder.
 *
 *   - `Masked`: each local-part label and the first domain label reduced
 *     ("fred.bloggs@foo.com" -> "f*.b*@f*.com", "fred@mail.foo.com" -> "f*@m*.foo.com").
 */
export function maskEmailAtFormat(email: string, format: EmailFormat): string {
  switch (format) {
    case "Full":
      return email;
    case "Hidden":
      return "";
    case "Masked": {
      const at = email.indexOf("@");
      if (at < 0) return maskDotted(email);
      return maskDotted(email.substring(0, at)) + "@" + maskDomain(email.substring(at + 1));
    }
  }
}

/** Reduce every dot-separated label to first character plus `*`. */
function maskDotted(part: string): string {
  return part.split(".").map(reduceLabel).join(".");
}

/** Reduce only the first dot-separated label, keeping the rest verbatim. */
function maskDomain(domain: string): string {
  const labels = domain.split(".");
  if (labels.length === 0) return domain;
  return [reduceLabel(labels[0]), ...labels.slice(1)].join(".");
}

/** First code point of the label plus `*`; empty stays empty. */
function reduceLabel(label: string): string {
  if (!label) return label;
  const cp = label.codePointAt(0);
  return cp === undefined ? label : String.fromCodePoint(cp) + "*";
}
